import type { EventEmitter } from '@stencil/core';
import { Component, Element, Event, Host, Listen, Prop, Watch, h } from '@stencil/core';
import type { AnchorInterface } from '../../utils/elements-interface';
import { generateAnchorAttributes, openURL } from '../../utils/elements-interface';

const OLD_TYPES = ['accent', 'basic', 'default', 'error', 'primary', 'success'];

/**
 * @slot - If the label is not defined, it is rendered in its place.
 */
@Component({
  tag: 'sy-button',
  styleUrl: 'scss/index.scss',
  shadow: {
    delegatesFocus: true,
  },
})
export class Button implements AnchorInterface {
  @Element() el: HTMLSyButtonElement;

  /**
     Sets the button to a "loading" state, making it display a loading spinner.
     If the button has an icon, the spinner will replace the icon.
     If not, the spinner will replace the button's label.
    */
  @Prop({ reflect: true }) loading = false;

  /**
     Sets the button as a fab.
    */
  @Prop({ reflect: true }) fab = false;

  /**
     HTML <a> download attribute
    */
  @Prop() download: string | undefined;

  /**
   * HTML <a> href attribute
   */
  @Prop() href: string | undefined;

  /**
   * HTML <a> rel attribute
   */
  @Prop() rel: string | undefined;

  /**
   * HTML <a> target attribute
   */
  @Prop() target: '_blank' | '_self' | '_parent' | '_top' = '_blank';

  /**
   * Sets button's type.
   */
  @Prop() type: 'button' | 'reset' | 'submit' | 'check' | 'radio' = 'button';

  /**
   * Sets button's content type
   */
  @Prop({ reflect: true }) content: 'text-only' | 'icon-left' | 'icon-only' | 'icon-right';

  /**
   * Optional button's name
   */
  @Prop() name: string;

  /**
   * Optional button's label
   */
  @Prop() label: string;

  /**
   * Icon name (use stardard lib)
   */
  @Prop({ reflect: true }) icon: string;

  /**
   * Decrease its internal padding.
   */
  @Prop({ reflect: true }) small = false;

  /**
   * Sets button's state to disabled
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * Sets button's state to active
   */
  @Prop({ reflect: true, mutable: true }) checked = false;

  /**
   * Sets button's variant
   */
  @Prop({ reflect: true }) variant: 'filled' | 'outlined' | 'clear' = 'clear';

  /**
   * Sets button's color
   */
  @Prop({ reflect: true }) color?: 'primary' | 'secondary' | 'error' | 'success' | 'info' | 'warning';

  /**
   * Custom aria-label for the button
   */
  @Prop({ reflect: true }) accessibleLabel: string;

  /**
   * Emitted after the button is checked.
   */
  @Event() syButtonDidCheck: EventEmitter<void>;

  /**
   * Emitted after the button is unchecked.
   */
  @Event() syButtonDidUncheck: EventEmitter<void>;

  @Watch('type')
  async typeWatcher(): Promise<void> {
    if (OLD_TYPES.includes(this.type)) {
      console.warn('The type prop has been replace by a combination of the variant and color props.');
    }

    if (this.type === 'radio') {
      this.addButtonRadioListener();
    } else {
      this.removeButtonRadioListener();
    }
  }

  @Watch('checked')
  async checkedWatcher(): Promise<void> {
    if (this.type === 'check' || this.type === 'radio') {
      if (this.checked) this.syButtonDidCheck.emit();
      else this.syButtonDidUncheck.emit();
    }
  }

  // Used event listener with capture so the event is stopped before other listeners.
  @Listen('click', { capture: true })
  onClickButton(event: Event): void {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private buttonRadioEvent = false;

  private contentClasses = {
    'text-only': 'sy-btn-text-only',
    'icon-left': 'sy-btn-icon-left',
    'icon-right': 'sy-btn-icon-right',
    'icon-only': 'sy-btn-icon-only',
  };

  private getButtonAttributes() {
    const canInteract = !this.disabled && !this.loading;
    const anchorAtributes =
      canInteract && this.href && this.type !== 'check' && this.type !== 'radio' ? generateAnchorAttributes(this) : {};

    const smallClass = this.small ? 'sy-btn-sm ' : '';
    const checkedClass = this.checked ? 'checked ' : '';
    let attributes: any = {
      ...anchorAtributes,
      class: `sy-btn ${smallClass} ${checkedClass} ${this.contentClasses[this.content]}`,
      'aria-disabled': this.disabled ? 'true' : null,
    };

    if (this.accessibleLabel || this.label) {
      attributes['aria-label'] = this.accessibleLabel ?? this.label;
    }

    if (this.type === 'check' || this.type === 'radio') {
      attributes['aria-checked'] = this.checked ? 'true' : 'false';
    }

    if (this.type === 'check') {
      attributes['role'] = 'checkbox';
    } else if (this.type === 'radio') {
      attributes['role'] = 'radio';
    }

    attributes = this.disabled ? { ...attributes, disabled: true } : attributes;

    return attributes;
  }

  private handleClick = (event: Event) => {
    if (this.type === 'check' || this.type === 'radio') {
      this.checked = !this.checked;
    } else if (this.type === 'button' && this.href) {
      openURL(this.href, event);
    } else {
      const form: HTMLFormElement | null = this.el.closest('form');
      if (form) {
        event.preventDefault();
        if (this.type === 'reset') {
          form.reset();
        } else if (this.type === 'submit') {
          form.requestSubmit();
        }
      }
    }
  };

  private addButtonRadioListener() {
    if (this.type === 'radio' && !this.buttonRadioEvent) {
      this.el.getRootNode().addEventListener('syButtonDidCheck', this.handleButtonRadioToggle);
      this.buttonRadioEvent = true;
    }
  }

  private removeButtonRadioListener() {
    if (this.buttonRadioEvent) {
      this.el.getRootNode().removeEventListener('syButtonDidCheck', this.handleButtonRadioToggle);
      this.buttonRadioEvent = false;
    }
  }

  private handleButtonRadioToggle = (event: Event) => {
    if (this.type === 'radio' && this.name && event.target !== this.el) {
      const button = event.target as HTMLSyButtonElement;
      if (button?.type === 'radio' && button.name === this.name) {
        this.checked = false;
      }
    }
  };

  private setDefaultContent(): void {
    if (!this.content) {
      this.content = this.icon ? 'icon-left' : 'text-only';
    }
  }

  componentWillLoad() {
    this.setDefaultContent();
  }

  connectedCallback() {
    this.addButtonRadioListener();
  }

  disconnectedCallback() {
    this.removeButtonRadioListener();
  }

  private renderInnerButton() {
    return (
      <div class="sy-btn-inner-content">
        {((this.content !== 'icon-right' && (this.loading || this.content === 'text-only')) ||
          (!this.loading && this.icon && (this.content === 'icon-left' || this.content === 'icon-only'))) && (
          <div class="sy-btn-inner-content-left">
            {!this.loading && this.icon && (this.content === 'icon-left' || this.content === 'icon-only') && (
              <sy-icon class="sy-btn-icon" name={this.icon}></sy-icon>
            )}
            {this.loading && this.renderLoadingSpinner()}
          </div>
        )}
        {this.content !== 'icon-only' && this.renderLabel()}
        {this.content === 'icon-right' && (
          <div class="sy-btn-inner-content-right">
            {!this.loading && this.icon && <sy-icon class="sy-btn-icon" name={this.icon}></sy-icon>}
            {this.loading && this.renderLoadingSpinner()}
          </div>
        )}
      </div>
    );
  }

  private renderLabel() {
    return this.label ? (
      <p class="sy-btn-inner-content-text" title={this.label}>
        {this.label}
      </p>
    ) : (
      <slot></slot>
    );
  }

  private renderLoadingSpinner() {
    return (
      <div class={`sy-loading-box ${this.loading && this.content === 'text-only' ? 'icon-left' : this.content}`}>
        <sy-loading size="small"></sy-loading>
      </div>
    );
  }

  render() {
    return (
      <Host onClick={this.handleClick}>
        {this.href && this.type !== 'check' && this.type !== 'radio' ? (
          <a {...this.getButtonAttributes()}>{this.renderInnerButton()}</a>
        ) : (
          <button {...this.getButtonAttributes()}>{this.renderInnerButton()}</button>
        )}
      </Host>
    );
  }
}
